import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Alert from "../../components/common/Alert";
import Table from "../../components/common/Table";
import PageHeader from "../../components/common/PageHeader";
import Button from "../../components/common/Button";
import FilterBar from "../../components/reporting/FilterBar";
import {
  useExportFulfillmentOrdersMutation,
  useGetFulfillmentOrdersQuery,
} from "../../store/api";
import { formatApiDateTime } from "../../utils/dateTime";
import { downloadBlob } from "../../utils/downloadFile";

const normalizeSearchValue = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const orderMatchesSearch = (order, normalizedQuery) => {
  if (!normalizedQuery) return true;

  const collect = [
    order?.orderId,
    order?.orderNumber,
    order?.orderNo,
    order?.status,
    order?.displayStatus,
    order?.orderType,
    order?.channel,
    order?.placedAt,
    order?.tableNo,
    order?.customerName,
    order?.masterTokenNo,
  ];

  const lineTokens = Array.isArray(order?.lines)
    ? order.lines.flatMap((line) => [
        line?.itemName,
        line?.itemId,
        line?.orderLineId,
        line?.stationCode,
      ])
    : [];

  const stationTokens = Array.isArray(order?.stationTokens)
    ? order.stationTokens.flatMap((token) => [
        token?.tokenNo,
        token?.stationCode,
        token?.status,
      ])
    : [];

  const haystack = [...collect, ...lineTokens, ...stationTokens]
    .map(normalizeSearchValue)
    .filter(Boolean)
    .join("|");

  return haystack.includes(normalizedQuery);
};

const FulfillmentOrders = ({
  embedded = false,
  compact = false,
  showFilters = true,
  showDownloadButton = true,
  status: controlledStatus,
  onStatusChange,
  search: controlledSearch,
  onSearchChange,
  startDate: controlledStartDate,
  onStartDateChange,
  endDate: controlledEndDate,
  onEndDateChange,
} = {}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(controlledStatus ?? "");
  const resolvedStatus = controlledStatus ?? status;

  const [search, setSearch] = useState(controlledSearch ?? "");
  const resolvedSearch = controlledSearch ?? search;

  const [startDate, setStartDate] = useState(controlledStartDate ?? (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  })());
  const resolvedStartDate = controlledStartDate ?? startDate;

  const [endDate, setEndDate] = useState(controlledEndDate ?? (() => new Date())());
  const resolvedEndDate = controlledEndDate ?? endDate;

  const setStatusValue = (value) => {
    setStatus(value);
    onStatusChange && onStatusChange(value);
  };
  const setSearchValue = (value) => {
    setSearch(value);
    onSearchChange && onSearchChange(value);
  };
  const setStartDateValue = (value) => {
    setStartDate(value);
    onStartDateChange && onStartDateChange(value);
  };
  const setEndDateValue = (value) => {
    setEndDate(value);
    onEndDateChange && onEndDateChange(value);
  };

  const query = useMemo(
    () => ({
      status: resolvedStatus || undefined,
      // Backend search does not consistently match order numbers;
      // keep client-side search and fetch unfiltered results.
      search: undefined,
      from: resolvedStartDate ? resolvedStartDate.toISOString() : undefined,
      to: resolvedEndDate ? resolvedEndDate.toISOString() : undefined,
      page: 1,
      pageSize: 100,
    }),
    [resolvedStatus, resolvedStartDate, resolvedEndDate],
  );

  const {
    data: ordersData,
    isLoading,
    isError,
    error,
  } = useGetFulfillmentOrdersQuery(query);
  const [exportOrders, { isLoading: isExporting }] =
    useExportFulfillmentOrdersMutation();

  const exportQuery = useMemo(
    () => ({
      status: resolvedStatus || undefined,
      search: resolvedSearch || undefined,
      from: resolvedStartDate ? resolvedStartDate.toISOString() : undefined,
      to: resolvedEndDate ? resolvedEndDate.toISOString() : undefined,
    }),
    [resolvedStatus, resolvedSearch, resolvedStartDate, resolvedEndDate],
  );

  const handleDownload = async () => {
    const result = await exportOrders(exportQuery);
    if (!("data" in result)) {
      return;
    }

    const contentDisposition = result?.meta?.response?.headers?.get(
      "content-disposition",
    );
    downloadBlob({
      blob: result.data,
      filename: `fulfillment-orders-${new Date().toISOString().slice(0, 10)}.xlsx`,
      contentDisposition,
    });
  };

  const orders = Array.isArray(ordersData?.orders)
    ? ordersData.orders
    : Array.isArray(ordersData)
      ? ordersData
      : [];
  const normalizedSearch = normalizeSearchValue(resolvedSearch);
  const filteredOrders = normalizedSearch
    ? orders.filter((order) => orderMatchesSearch(order, normalizedSearch))
    : orders;

  const compactHeader = compact ? "py-1 text-[11px]" : "";
  const compactCell = compact ? "py-0.5 text-xs" : "";
  const columns = [
    { key: "orderId", label: "Order ID", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "orderNumber", label: "Order Number", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "status", label: "Status", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "channel", label: "Channel", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "placedAt", label: "Placed At", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "actions", label: "Actions", headerClassName: compactHeader, cellClassName: compactCell },
  ];

  const rows = filteredOrders.map((order, index) => {
    return {
      id: order.orderId,
      className: compact
        ? [
            "border-t border-slate-200/80 dark:border-slate-700",
            index % 2 === 0
              ? "bg-white dark:bg-slate-800"
              : "bg-slate-50/50 dark:bg-slate-800/70",
            "hover:bg-slate-100/70 dark:hover:bg-slate-700/60",
          ].join(" ")
        : undefined,
      orderId: (
        <span
          className={
            compact
              ? "text-xs font-semibold text-slate-900 dark:text-slate-100"
              : "font-semibold text-slate-900 dark:text-slate-100"
          }
        >
          {order.orderNumber || order.orderNo || order.orderId || "-"}
        </span>
      ),
      orderNumber: (
        <span className={compact ? "text-xs text-slate-700 dark:text-slate-300" : ""}>
          {order.masterTokenNo || "-"}
        </span>
      ),
      status: (
        <span className={compact ? "text-xs text-slate-700 dark:text-slate-300" : ""}>
          {order.displayStatus || order.status || "-"}
        </span>
      ),
      channel: (
        <span className={compact ? "text-xs text-slate-700 dark:text-slate-300" : ""}>
          {order.channel ||
            order.orderChannel ||
            order.channelName ||
            order.sourceChannel ||
            "-"}
        </span>
      ),
      placedAt: (
        <span className={compact ? "text-xs text-slate-700 dark:text-slate-300" : ""}>
          {formatApiDateTime(order.placedAt)}
        </span>
      ),
      actions: (
        <button
          type="button"
          onClick={() => navigate(`/fulfillment/orders/${order.orderId}`)}
          className="rounded p-0.5 text-indigo-600 transition hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
          title="View"
          aria-label="View"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        </button>
      ),
    };
  });

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"}>
      {!embedded && (
        <PageHeader
          title="Fulfillment Orders"
          subtitle="Monitor station-wise fulfillment and token distribution."
          actions={
            <Button size="sm" onClick={handleDownload} disabled={isExporting}>
              {isExporting ? "Downloading..." : "Download"}
            </Button>
          }
        />
      )}

      <Card
        className={
          compact
            ? "p-0 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm rounded-xl overflow-hidden"
            : "space-y-4"
        }
      >
        {(showFilters || showDownloadButton) && (
          <div
            className={
              compact
                ? "flex items-center justify-end border-b border-slate-200 px-2 py-2 dark:border-slate-700"
                : "flex flex-wrap items-start justify-between gap-2"
            }
          >
            {showFilters && (
              <div className={compact ? "w-full pt-1" : "min-w-[260px] flex-1"}>
                <FilterBar
                  fieldOrder={["search", "from", "to", "status"]}
                  startDate={resolvedStartDate}
                  endDate={resolvedEndDate}
                  onStartDateChange={setStartDateValue}
                  onEndDateChange={setEndDateValue}
                  showChannel={false}
                  showSearch
                  search={resolvedSearch}
                  onSearchChange={setSearchValue}
                  searchPlaceholder="Enter the order number or order ID"
                  showStatus
                  status={resolvedStatus}
                  onStatusChange={setStatusValue}
                  statusOptions={[
                    { value: "", label: "All" },
                    { value: "PREPARING", label: "PREPARING" },
                    { value: "CAPTURED", label: "CAPTURED" },
                    { value: "PAYMENT_FAILED", label: "PAYMENT_FAILED" },
                    { value: "COMPLETED", label: "COMPLETED" },
                  ]}
                  onReset={() => {
                    const now = new Date();
                    setStatusValue("");
                    setSearchValue("");
                    setStartDateValue(
                      new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0),
                    );
                    setEndDateValue(now);
                  }}
                />
              </div>
            )}

            {showDownloadButton && (
              <div className="shrink-0">
                <Button size="sm" onClick={handleDownload} disabled={isExporting}>
                  {isExporting ? "Downloading..." : "Download"}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className={compact ? "px-2 pt-0 pb-2 md:px-2.5" : ""}>
        {isLoading && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading fulfillment orders...
          </p>
        )}
        {isError && (
          <Alert tone="error">
            {error?.data?.detail ||
              error?.data?.message ||
              "Failed to load fulfillment orders."}
          </Alert>
        )}
        {!isLoading && !isError && (
          <Table
            columns={columns}
            rows={rows}
            emptyText="No fulfillment orders."
          />
        )}
        </div>
      </Card>
    </div>
  );
};

export default FulfillmentOrders;
